import {
  AnalyticsClientApiConnectionArgs,
  ApiConnectionArgs
} from '@dtx-company/inter-app/src/types/analytics'
import {
  EventType,
  RangeSummaryData
} from '@dtx-company/connect-codegen/src/gen/analytics/v2/analytics_pb'
import {
  GetAnalyticsSummary,
  GetConnections,
  GetDayOfWeek,
  GetHourOfDay,
  GetTopCities,
  GetTopOperatingSystems,
  GetTopStates
} from '../../../types/chart'
import { Timestamp } from '@bufbuild/protobuf'
import { getHeaders, prepParams, baseApiUri as uri } from '../utils'
import { logger } from '@dtx-company/logger'
import analyticsClient from '@dtx-company/inter-app/src/connect-client/analyticsClient'

/**
 *
 * Fetches range summary data for Flowcode or Flowpage
 */
export async function getRangeSummary(
  args: AnalyticsClientApiConnectionArgs
): Promise<Partial<RangeSummaryData> | undefined> {
  try {
    const eid: EventType[] =
      typeof args.params.event_type_id === 'number'
        ? [args.params.event_type_id]
        : (args.params.event_type_id as EventType[])

    const resp = await analyticsClient.rangeSummary(
      {
        eventType: eid,
        allAssets: true,
        product: args.params.product_id,
        startDate: Timestamp.fromDate(new Date(args.params.start_date || '')),
        endDate: Timestamp.fromDate(new Date(args.params.end_date || ''))
      },
      { headers: { Authorization: 'Bearer ' + args.token?.token } }
    )
    return resp.data
  } catch (e) {
    console.error('error fetching range summary: ', e.message)
    logger.logError(e, { team: 'flowlytics', tag: 'welcome-toast' })
    return undefined
  }
}

/**
 *
 * Fetches base connections data for Flowcode or Flowpage
 */
export async function getConnections(args: ApiConnectionArgs): Promise<GetConnections | null> {
  try {
    const data = await (
      await fetch(`${uri}/date-range/v2?${prepParams(args.params)}`, {
        method: 'GET',
        headers: getHeaders(args.token)
      })
    ).json()

    return data.body
  } catch (e) {
    console.error('error fetching connections chart: ', e.message)
    return null
  }
}

/**
 *
 * Fetches top connections data by hour
 */
export async function getHourOfDay(args: ApiConnectionArgs): Promise<GetHourOfDay | null> {
  try {
    const data = await (
      await fetch(`${uri}/top/hours/v2?${prepParams(args.params)}`, {
        method: 'GET',
        headers: getHeaders(args.token)
      })
    ).json()
    return data.body ?? data
  } catch (e) {
    console.error('error in analytics getTimeOfDay: ', e.message)
    return null
  }
}

/**
 *
 * Fetches top connections by day of the week
 */
export async function getDayOfWeek(args: ApiConnectionArgs): Promise<GetDayOfWeek | null> {
  try {
    const data = await (
      await fetch(`${uri}/top/days/v2?${prepParams(args.params)}`, {
        method: 'GET',
        headers: getHeaders(args.token)
      })
    ).json()
    return data.body ?? data
  } catch (e) {
    console.error('error in analytics getDayOfWeek: ', e.message)
    return null
  }
}

/**
 *
 * Fetches top connections by operating system
 * Also currently used for "device type" charts
 */
export async function getTopOperatingSystems(
  args: ApiConnectionArgs
): Promise<GetTopOperatingSystems | null> {
  try {
    const data = await (
      await fetch(`${uri}/top/operating-systems/v2?${prepParams(args.params)}`, {
        method: 'GET',
        headers: getHeaders(args.token)
      })
    ).json()
    return data.body ?? data
  } catch (e) {
    console.error('error in analytics getTopOperatingSystems: ', e.message)
    return null
  }
}

/**
 *
 * Fetches top connections by city
 */
export async function getTopByCity(args: ApiConnectionArgs): Promise<GetTopCities | null> {
  try {
    const data = await (
      await fetch(`${uri}/top/cities/v2?${prepParams(args.params)}`, {
        method: 'GET',
        headers: getHeaders(args.token)
      })
    ).json()
    return data.body ?? data
  } catch (e) {
    console.error('error getting top cities data: ', e.message)
    return null
  }
}

/**
 *
 * Fetches top connections by state
 */
export async function getTopByState(args: ApiConnectionArgs): Promise<GetTopStates | null> {
  try {
    const data = await (
      await fetch(`${uri}/top/states/v2?${prepParams(args.params)}`, {
        method: 'GET',
        headers: getHeaders(args.token)
      })
    ).json()
    return data.body ?? data
  } catch (e) {
    console.error('error getting top states data: ', e.message)
    return null
  }
}

/**
 *
 * Fetches a zip file for a user's data report as CSV files
 */
export interface ExportReportOptions {
  exportType?: string
}
export async function exportFlowlyticsReport(
  args: ApiConnectionArgs,
  options?: ExportReportOptions
): Promise<string | null> {
  const exportType = options?.exportType || 'v2'

  try {
    const resp = await fetch(`${uri}/download/${exportType}?${prepParams(args.params)}`, {
      method: 'GET',
      headers: getHeaders(args.token)
    })
    const blob = await resp.blob()
    return URL.createObjectURL(blob)
  } catch (e) {
    console.error('Error generating CSV download: ', e.message)
    return null
  }
}

export async function getAnalyticsSummary(
  args: ApiConnectionArgs
): Promise<GetAnalyticsSummary | null> {
  try {
    const data = await (
      await fetch(`${uri}/summary/v4?${prepParams(args.params)}`, {
        method: 'GET',
        headers: getHeaders(args.token)
      })
    ).json()
    return data.summary_v3
  } catch (e) {
    console.error('error getting analytics summary: ', e.message)
    return null
  }
}
